/* @flow */
import * as React from 'react';
import styled from 'styled-components';

import { Box, Flex, Text } from '@sonyged/proc-ui';
import { ProcModal, Button, Footer } from '@sonyged/ext-proc';
import css from '@styled-system/css';

import { Pizza } from '../../../pizza/fields/domain/pizza';
import { Random, Rng } from '../../../../utils/Random';
import { genComments, pizzaG } from '../game';
import { ingredientName } from '../constants';
import AutoScaleContainer from '../../../pizza/fields/AutoScaleContainer';
import IconImg from '../../../../../assets/images/pages/sushi/icon_sushi_field_man.svg';
import PizzaContainer from '../../../pizza/fields/PizzaContainer';

let random = comments => {
  if (comments.length === 0) return null;
  const random = Math.floor(Math.random() * (comments.length - 1));
  return comments[random];
};

type Comment = [0 | 1, string];
let pizzasWithCommentG = (yesterdayPizza): Random<Array<[Pizza, Comment]>> =>
  Random.arrayOfLength(pizzaG, 3)
    .map(pizzas => {
      let comments: Array<[0 | 1, string] | null> = [];
      return pizzas.reduce((acc, pizza) => {
        let pizzaComment = random(
          genComments(pizza, yesterdayPizza, comments.map(comment => (comment ? comment[1] : null)).filter(Boolean))
        );
        comments.push(pizzaComment);
        acc.push([pizza, pizzaComment || [0, '']]);
        return acc;
      }, []);
    })
    .filter(pizzaWithComments => {
      return pizzaWithComments.every(([, [, content]]) => Boolean(content));
    });

type Props = {
  isOpen: boolean,
  pizza: Pizza,
  onClose: Function,
};
export function HintModal(props: Props) {
  let { isOpen, pizza: yesterdayPizza, onClose } = props;

  let pizzaWithComments = React.useMemo(() => {
    return pizzasWithCommentG(yesterdayPizza).gen(new Rng(Math.random()));
  }, [yesterdayPizza]);

  return (
    <ProcModal isOpened={isOpen} rootNodeId="proc-pizza-fields-root-page">
      <div key="modal" className={'proc-modal proc-modal--hint'}>
        <div className="proc-modal-container">
          <div className={'proc-modal-header'}>{'お客さんアンケート'}</div>
          <Contents>
            <Overflow>
              {pizzaWithComments.map(([pizza, comment], index) => (
                <CheckPizzaPanel key={index}>
                  <Flex alignItems="center" flexDirection="column">
                    <CheckPizzaTitle>当日のピザ</CheckPizzaTitle>
                    <div style={{ width: '50%' }}>
                      <ProcResultSalePizza>
                        <AutoScaleContainer preferedHeight={600} preferedWidth={1080} preferedMinWidth={1080}>
                          <PizzaContainer pizza={pizza} />
                        </AutoScaleContainer>
                      </ProcResultSalePizza>
                    </div>
                  </Flex>
                  <Flex alignItems="center" style={{ marginTop: 7 }}>
                    <CustomerAvatar src={IconImg} />
                    <CustomerComment>
                      {'お客さんのひょうか '}
                      <Text color={comment[0] > 0 ? '#f0875a' : '#1e5aa5'}>
                        {comment[0] > 0 ? 'よかった' : '残念だった'}
                      </Text>
                    </CustomerComment>
                  </Flex>
                  <CommentDetail>{comment[1]}</CommentDetail>
                  <IngredientContainer>
                    <IngredientList>
                      <CheckPizzaTitle>前日</CheckPizzaTitle>

                      {[yesterdayPizza.crust, yesterdayPizza.sauce]
                        .concat(yesterdayPizza.toppings)
                        .map((ingredient, i) => (
                          <div key={i}>{ingredientName(ingredient)}</div>
                        ))}
                    </IngredientList>
                    <IngredientList>
                      <CheckPizzaTitle>当日</CheckPizzaTitle>
                      {[pizza.crust, pizza.sauce].concat(pizza.toppings).map((ingredient, i) => (
                        <div key={i}>{ingredientName(ingredient)}</div>
                      ))}
                    </IngredientList>
                  </IngredientContainer>
                </CheckPizzaPanel>
              ))}
            </Overflow>
          </Contents>
        </div>
        <Box>
          <Footer.normal shadow={false}>
            <Button.attention onClick={onClose}>とじる</Button.attention>
          </Footer.normal>
        </Box>
      </div>
    </ProcModal>
  );
}

const Contents = styled(Box)(
  css({
    flex: 1,
    minHeight: 0,
    px: [16, 16, 0], // because proc-modal--hint
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  })
);

const Overflow = styled(Box)(
  css({
    flex: 1,
    minHeight: 0,
    py: [16, 16, 24],
    overflow: 'auto',
    maskImage: [
      'linear-gradient(to top, transparent 0%, black 16px, black calc(100% - 16px),  transparent 100%)',
      'linear-gradient(to top, transparent 0%, black 16px, black calc(100% - 16px),  transparent 100%)',
      'linear-gradient(to top, transparent 0%, black 26px, black calc(100% - 26px),  transparent 100%)',
    ],
  })
);

const CheckPizzaPanel = styled.div(
  css({
    bg: 'white',
    px: [12, 12, 48],
    py: [18, 18, 26],
    border: '1px solid #9b6441',
    borderRadius: [8, 8, 12],
    marginBottom: '20px',
  })
);

const CheckPizzaTitle = styled(Box)(
  css({
    fontSize: ['17px', '17px', '28px'],
    color: '#9b6441',
  })
);

const ProcResultSalePizza = styled(Box)(
  css({
    '.pizza-viewport-inner': {
      position: 'relative !important',
    },
    '&&& .pizza-image.pizza-container': {
      top: 0,
      left: 0,
      right: 0,
      size: '100%',
    },
  })
);

const CustomerAvatar = styled('img')(
  css({
    size: ['28px', '28px', '42px'],
  })
);

const CustomerComment = styled(Box)(
  css({
    color: 'gray100',
    fontSize: ['16px', '16px', '18px'],
  })
);

const CommentDetail = styled(Box)(
  css({
    position: 'relative',
    backgroundColor: '#f7f3f0',
    borderRadius: ['4px', '4px', '5px'],
    padding: '8px 10px',
    fontSize: ['14px', '14px', '18px'],
    color: 'gray100',
    marginTop: '7px',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-5px',
      left: ['9px', '9px', '19px'],
      width: 0,
      height: 0,
      borderRight: '5px solid transparent',
      borderLeft: '5px solid transparent',
      borderBottom: '5px solid #f7f3f0',
    },
  })
);

const IngredientContainer = styled(Flex)(
  css({
    borderTop: '1px solid #9b6441',
    mt: [12, 12, 18],
    mx: [-12, -12, -48],
    mb: [-18, -18, -26],
  })
);

const IngredientList = styled(Box)(
  css({
    width: '50%',
    px: [12, 12, 48],
    py: [15, 15, 20],
    color: 'gray100',
    '&:not(:last-child)': {
      borderRight: '1px solid #9b6441',
    },
  })
);
